import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';

/**
 * Table for an in-progress inventory session — allows entering actual counts
 * and adding new products via search.
 */
const ActiveInventoryTable = ({ inventoryData, onCountChange, onCancel, onFinish, onAddProduct, loading, locationId }) => {
  // Filtrowanie istniejącej listy
  const [filterText, setFilterText] = useState('');

  // Dodawanie nowych produktów
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filtrowana lista
  const filteredData = filterText.trim().length >= 2
    ? inventoryData.filter(item => {
        const search = filterText.toLowerCase();
        return (item.product_name || '').toLowerCase().includes(search) ||
               (item.product_code || '').toLowerCase().includes(search) ||
               (item.product_description || '').toLowerCase().includes(search);
      })
    : inventoryData;

  // Debounced API search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        let url = `/products/search?limit=15&query=${encodeURIComponent(searchQuery.trim())}`;
        if (locationId) url += `&location_id=${locationId}`;
        const response = await api.get(url);
        const products = response.data?.data?.products || response.data?.products || [];
        // Filter out products already in inventory
        const existingIds = new Set(inventoryData.map(i => i.product_id));
        const filtered = products.filter(p => !existingIds.has(p.id));
        setSearchResults(filtered);
        setShowDropdown(filtered.length > 0);
      } catch (err) {
        console.error('Błąd wyszukiwania:', err);
      }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery, locationId, inventoryData]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelectProduct = (product) => {
    if (onAddProduct) {
      onAddProduct(product);
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  };

  return (
    <div className="card" style={{ fontSize: '12px' }}>
      <div className="card-header d-flex justify-content-between align-items-center" style={{ padding: '0.4rem 0.75rem' }}>
        <h5 className="card-title mb-0" style={{ fontSize: '13px', fontWeight: '600' }}>
          Inwentaryzacja w toku
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '8px' }}>
            ({inventoryData.length} pozycji)
          </span>
        </h5>
        <div>
          <button
            style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', marginRight: '0.5rem' }}
            onClick={onCancel}
          >
            <i className="fas fa-times me-1"></i>Anuluj
          </button>
          <button
            style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' }}
            onClick={onFinish}
            disabled={loading}
          >
            <i className="fas fa-check me-1"></i>Zakoncz inwentaryzacje
          </button>
        </div>
      </div>
      <div className="card-body" style={{ padding: '0.5rem 0.75rem' }}>
        {/* Wyszukiwarka produktów */}
        <div ref={dropdownRef} style={{ position: 'relative', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <i className="fas fa-plus-circle" style={{ color: 'var(--success)', fontSize: '16px' }}></i>
            <input
              type="text"
              style={{
                flex: 1, fontSize: '12px', padding: '6px 10px',
                border: '2px solid var(--success)', borderRadius: '6px',
                backgroundColor: 'var(--card-bg)', color: 'var(--text)'
              }}
              placeholder="Dodaj produkt — wpisz nazwe, kod EAN lub kod produktu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
            />
          </div>

          {/* Dropdown wyników */}
          {showDropdown && searchResults.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: '24px', right: 0,
              maxHeight: '250px', overflowY: 'auto',
              backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
              borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000
            }}>
              {searchResults.map(product => (
                <div
                  key={product.id}
                  onClick={() => handleSelectProduct(product)}
                  style={{
                    padding: '0.5rem 0.75rem', cursor: 'pointer',
                    borderBottom: '1px solid var(--bg)', fontSize: '11px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div>
                    <strong>{product.name || product.nazwa}</strong>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                      EAN: {product.barcode || product.ean || '-'} | Kod: {product.product_code || product.kod_produktu || '-'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '10px' }}>
                    <span style={{
                      padding: '1px 6px', borderRadius: '3px',
                      backgroundColor: (product.stock_quantity || 0) > 0 ? 'var(--success)' : 'var(--text-muted)',
                      color: '#fff'
                    }}>
                      {product.stock_quantity || 0} szt.
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filtrowanie listy */}
        <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <i className="fas fa-filter" style={{ color: 'var(--info)', fontSize: '14px' }}></i>
          <input
            type="text"
            style={{
              flex: 1, fontSize: '12px', padding: '6px 10px',
              border: '2px solid var(--info)', borderRadius: '6px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)'
            }}
            placeholder="Filtruj liste — wpisz nazwe, kod EAN lub kod produktu (min. 2 znaki)..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {filteredData.length} / {inventoryData.length}
            </span>
          )}
        </div>

        {/* Tabela pozycji */}
        <div className="table-responsive">
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['LP', 'Produkt', 'Kod', 'Stan systemowy', 'Stan rzeczywisty', 'Roznica'].map(h => (
                  <th key={h} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: h === 'LP' ? 'center' : 'left', fontWeight: '600', color: 'var(--text-secondary)', width: h === 'LP' ? '35px' : 'auto' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={item.product_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)' }}>{index + 1}</td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    <strong>{item.product_name}</strong>
                    {item.product_description && <><br /><small style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{item.product_description}</small></>}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{item.product_code || 'Brak'}</code>
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: 'var(--cyan)', color: '#fff', borderRadius: '3px' }}>
                      {item.system_count} {item.unit || 'szt'}
                    </span>
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    <input
                      type="number"
                      style={{ width: '80px', fontSize: '11px', padding: '0.2rem 0.4rem', border: '1px solid var(--border)', borderRadius: '3px' }}
                      value={item.actual_count || ''}
                      onChange={(e) => onCountChange(item.product_id, e.target.value)}
                      min="0"
                      placeholder="0"
                    />
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    {item.actual_count !== undefined && item.actual_count !== null && (
                      <span style={{
                        fontSize: '10px', padding: '2px 6px', color: '#fff', borderRadius: '3px',
                        backgroundColor: (item.actual_count - item.system_count) === 0 ? 'var(--success)'
                          : (item.actual_count - item.system_count) > 0 ? 'var(--warning)' : 'var(--danger)'
                      }}>
                        {item.actual_count - item.system_count > 0 ? '+' : ''}{item.actual_count - item.system_count}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ActiveInventoryTable;
